<template>
  <div class="flex w-full">
    <DefaultLink
      :to="getProductDetailRoute(product)"
      raw
      class="relative mr-3 flex w-32 items-center rounded-md bg-gray-200"
    >
      <ProductImage
        v-if="image"
        :image="image"
        :alt="name"
        :image-loading="eagerImageLoading ? 'eager' : 'lazy'"
        sizes="xl:100vw lg:100vw lg:100vw lg:100vw xs:100vw"
        fit="cover"
        :class="{
          grayscale: !areGiftConditionsMet,
        }"
      />
      <ProductPromotionFreeGiftBadge
        :style="promotionStyle"
        class="absolute bottom-2 left-2"
      />
    </DefaultLink>
    <div class="flex w-full flex-col justify-between">
      <Headline
        size="base"
        tag="h3"
        class="mt-2"
        :class="{
          'text-secondary': !areGiftConditionsMet,
        }"
        >{{ name }}</Headline
      >
      <div class="flex items-end justify-between">
        <AppButton
          size="sm"
          :disabled="!isProductAddedToBasket || !areGiftConditionsMet"
          @click="toggleGiftSelection()"
        >
          {{ $t('pdp.promotion.add_for_free_label') }}
        </AppButton>
        <div class="flex flex-col items-end">
          <span
            v-if="variantWithLowestPrice"
            class="text-xs text-gray-600 line-through"
            :class="{
              'text-secondary': !areGiftConditionsMet,
            }"
          >
            {{ formatCurrency(variantWithLowestPrice.price.withTax) }}
          </span>
          <span
            class="inline rounded-md p-1 text-base font-medium leading-5"
            :class="{
              'text-secondary ': !areGiftConditionsMet,
            }"
            :style="promotionStyle"
          >
            {{ formatCurrency(0) }}
          </span>
          <span
            class="text-xs text-gray-700"
            :class="{
              'text-secondary': !areGiftConditionsMet,
            }"
          >
            {{ $t('price.including_vat') }}
          </span>
        </div>
      </div>
    </div>
    <template v-if="promotion && product && promotedProduct">
      <ProductPromotionSelectionModal
        v-if="isGreaterOrEquals('md')"
        v-bind="{ product, promotion, promotedProduct }"
      />
      <ProductPromotionSizeSelection
        v-else
        v-bind="{ product, promotion, promotedProduct }"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-nuxt'

const props = defineProps<{
  product: Product
  promotionStyle: Promotion['customData']['style']
  isProductAddedToBasket: boolean
  eagerImageLoading: boolean
}>()

const { product: promotedProduct } = await useProductDetails(props.product)
const { formatCurrency } = useFormatHelpers()

const { isGreaterOrEquals } = useViewport()

const { buyXGetYPromotion: promotion, areGiftConditionsMet } =
  await useProductPromotions(promotedProduct)

const { toggleGiftSelection } = await usePromotionGiftSelection(
  props.product,
  promotedProduct.value,
)

const { getProductDetailRoute } = useRouteHelpers()

const { name, image, variantWithLowestPrice } = useProductBaseInfo(
  props.product,
)
</script>
